import {
  Sanity$GridItem,
  Sanity$GridStylingOptions,
} from '@otovo/shared/types/sanityTypes';
import cx from 'classnames';
import { stegaClean } from 'next-sanity';

import PortableText from '@otovo/shared/components/Sanity/PortableTextComponents';
import SanityImage from '@otovo/shared/components/Sanity/SanityImage';
import ArrowRight from '@otovo/shared/images/icons/arrow-right.svg';

type Props = {
  items: Sanity$GridItem[];
  stylingOptions: Sanity$GridStylingOptions;
};

const ImageItems = ({ items, stylingOptions }: Props) => {
  const itemsPerRow = stegaClean(stylingOptions.itemsPerRow),
    equalizeImageHeight = stegaClean(stylingOptions.equalizeImageHeight),
    imageWidth = stegaClean(stylingOptions.imageWidth);

  const imgWidth = {
    small: 'w-24',
    medium: 'w-64',
    large: 'w-full max-w-lg',
  }[imageWidth];

  const shouldShowCallToAction = (item: Sanity$GridItem) =>
    !!item.url && !!item.callToAction;

  return (
    <ul
      className={cx(
        'grid w-full grid-cols-1 gap-x-7 gap-y-10 md:gap-y-7',
        `${itemsPerRow.medium === 2 && 'sm:grid-cols-2'}`,
        `${itemsPerRow.medium === 3 && 'sm:grid-cols-3'}`,
        `${itemsPerRow.medium === 4 && 'sm:grid-cols-4'}`,
        `${itemsPerRow.large === 2 && 'lg:grid-cols-2'}`,
        `${itemsPerRow.large === 3 && 'lg:grid-cols-3'}`,
        `${itemsPerRow.large === 4 && 'lg:grid-cols-4'}`,
      )}
    >
      {(items || []).map((item) => {
        return (
          <li
            key={item._key}
            className="relative w-full max-w-xl list-none md:mb-0"
          >
            <div
              className={`shrink-0 ${imgWidth} relative mb-6 aspect-3/2 overflow-hidden rounded-lg`}
            >
              <SanityImage
                className="h-full rounded-xl"
                image={item.image}
                alt={item.imageAltText}
                equalizeImageHeight={equalizeImageHeight}
                fill
                sizes="(max-width: 767px) 320px, 280px"
              />
            </div>
            <div className="space-y-3">
              {item.heading && (
                <h3 className="text-xl font-semibold text-balance text-subheading lg:text-2xl">
                  {item.heading}
                </h3>
              )}
              {item.description && <PortableText value={item.description} />}
            </div>
            {shouldShowCallToAction(item) && (
              <div className="mt-4 font-semibold text-strong decoration-0">
                <a href={item.url} className="hover:underline">
                  {item.callToAction}
                  <ArrowRight className="mb-1 ml-1 inline-block" />
                </a>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default ImageItems;
